import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import {
  Form,
  Row,
  Col,
  FormInstance,
  Button,
  Input,
  Divider,
  Empty,
} from 'antd';
import moment from 'moment';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import {
  CompaniesInfiniteSelectField,
  InputDate,
  InputDecimal,
  InputSearch,
} from '@/components/_common';
import { getTableScrollX } from '@/utils/table';
import SuppliersSelectedModal from '@/components/_common/SuppliersSelectedModal';
import { SupplierReduced } from '@/core/domain/people/Suppliers/Suppliers.types';
import {
  PurchaseItem,
  ReturnItemFormValues,
  ReturnItemQuery,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';
import { useItemsPurchasesReturn } from '@/core/domain/purchase/purchaseItem/PurchaseItem.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import GenerateInvoiceButtons from '../GenerateInvoiceButtons';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { quantityFormatter } from '@/utils/number';

interface Props {
  form: FormInstance<ReturnItemFormValues>;
  onCallback: () => void;
  queryParams: ReturnItemQuery;
  generateInvoiceId: number;
  setGenerateInvoiceId: React.Dispatch<React.SetStateAction<number>>;
  setHasKey: (hasKey: boolean) => void;
}

function PurchaseReturnForm({
  form,
  onCallback,
  queryParams,
  generateInvoiceId,
  setGenerateInvoiceId,
  setHasKey,
}: Props) {
  const intl = useIntl();
  const [rowKeys, setRowKeys] = useState<number[]>([]);

  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const [modalSupplierVisible, modalSupplierAction] = useDisclosure();

  const {
    data,
    isFetching,
    fetchNextPage,
    hasNextPage,
    dataUpdatedAt,
    remove,
  } = useItemsPurchasesReturn({ ...queryParams });

  const itemsPurchaseReturn = flatInfiniteData(data);
  const startDate = Form.useWatch('releaseDateStart', form);

  useEffect(() => {
    if (queryParams.companyId && queryParams.supplierId) {
      if (itemsPurchaseReturn.length) {
        form.setFieldValue('items', itemsPurchaseReturn);
      } else {
        form.setFieldValue('items', []);
      }
      setRowKeys([]);
    }
  }, [dataUpdatedAt]);

  const items = Form.useWatch('items', form);

  const columns: ColumnsType<PurchaseItem> = [
    {
      key: 'workOrder',
      dataIndex: 'workOrder',
      title: intl.formatMessage({
        id: 'purchaseReturn.form.table.workOrder',
      }),
      ellipsis: true,
      width: 120,
    },
    {
      key: 'purchaseCode',
      dataIndex: ['purchase', 'code'],
      title: intl.formatMessage({
        id: `purchaseReturn.form.table.purchaseCode`,
      }),
      ellipsis: true,
      width: 120,
    },
    {
      key: 'purchaseDate',
      dataIndex: ['purchase', 'releaseDate'],
      title: intl.formatMessage({
        id: `purchaseReturn.form.table.purchaseDate`,
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      ellipsis: true,
      width: 100,
    },
    useConfigurationColumnsPart({
      internalCode: 'sku',
      manufacturingCode: 'manufacturingCode',
      barcode: 'barCode',
      companyCode: 'companyCode',
    }),

    {
      key: 'name',
      dataIndex: 'name',
      title: intl.formatMessage({
        id: `purchaseReturn.form.table.description`,
      }),
      ellipsis: true,
      width: 210,
    },
    {
      key: 'quantity',
      dataIndex: 'quantity',
      title: intl.formatMessage({
        id: `purchaseReturn.form.table.qtyPurchased`,
      }),
      render: (value) => quantityFormatter(value || 0),
      ellipsis: true,
      width: 120,
    },
    {
      key: 'amountReturned',
      dataIndex: 'amountReturned',
      title: intl.formatMessage({
        id: `purchaseReturn.form.table.qtyReturned`,
      }),
      render: (value) => value ?? 0,
      ellipsis: true,
      width: 120,
    },
    {
      key: 'quantityToReturn',
      title: intl.formatMessage({
        id: `purchaseReturn.form.table.qtyToReturn`,
      }),
      render(_, row, index: number) {
        return (
          <Form.Item
            style={{ margin: 0 }}
            name={[index, 'quantityToReturn']}
            rules={[
              {
                required: rowKeys.includes(row.id),
                message: 'Preencha o campo',
              },
              {
                type: 'number',
                min: 0.01,
                message: 'Preencha um número maior que 0!',
              },
            ]}
          >
            <InputDecimal max={row.quantity - (row.amountReturned ?? 0)} />
          </Form.Item>
        );
      },
      width: 120,
      fixed: 'right',
      align: 'right',
    },
  ];

  const handleSupplierSelect = (supplier?: SupplierReduced) => {
    form.setFieldsValue({
      supplier: {
        id: supplier?.id,
        name: supplier?.name,
      },
    });
    modalSupplierAction.close();

    form.setFieldsValue({ items: [] });
  };

  const resetDateError = () => {
    form.setFields([
      { name: 'releaseDateStart', errors: [] },
      { name: 'releaseDateEnd', errors: [] },
    ]);
  };

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        onValuesChange={(valuesChanged, fields) => {
          if (fields.items.length) {
            const fieldItems = fields.items.filter(
              (item) => typeof item?.quantityToReturn === 'number',
            );

            setHasKey(fieldItems.some(({ hasKey }) => hasKey));
            setRowKeys(fieldItems.map((item) => item.id));
          }

          if (valuesChanged.companyId || valuesChanged.issueDate) {
            form.setFieldsValue({ items: [] });
          }
        }}
      >
        <Row gutter={[16, 9]}>
          <Col span={24} md={12} lg={6} xxl={8}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'purchaseReturn.form.company',
              })}
              name="companyId"
              rules={[defaultEmptyFieldError]}
              initialValue={queryParams.companyId}
            >
              <CompaniesInfiniteSelectField queryFilter={{ active: true }} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6}>
            <Form.Item
              hidden
              name={['supplier', 'id']}
              initialValue={queryParams.supplierId}
            >
              <Input />
            </Form.Item>
            <FloatingFormItem
              name={['supplier', 'name']}
              label={intl.formatMessage({
                id: 'purchases.releases.form.supplier',
              })}
              initialValue={queryParams.supplierName}
              rules={[defaultEmptyFieldError]}
            >
              <InputSearch
                inputProps={{ disabled: true }}
                buttonProps={{ onClick: modalSupplierAction.open }}
              />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={6} lg={4} xxl={3}>
            <FloatingFormItem
              getValueFromEvent={(value) => {
                if (value > form.getFieldValue('releaseDateEnd')) {
                  form.setFieldValue('releaseDateEnd', undefined);
                }
                return value;
              }}
              label={intl.formatMessage({
                id: 'purchaseReturn.form.releaseDateOf.start',
              })}
              name="releaseDateStart"
              rules={[
                (e) => {
                  const startDate = e.getFieldValue('releaseDateStart');
                  const endDate = e.getFieldValue('releaseDateEnd');

                  if (!startDate && endDate) {
                    return { required: true, message: 'Informe a data' };
                  }
                  return { required: false };
                },
              ]}
              initialValue={
                queryParams.releaseDateStart
                  ? moment(queryParams.releaseDateStart)
                  : undefined
              }
            >
              <InputDate />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={6} lg={4} xxl={3}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'purchaseReturn.form.releaseDateOf.end',
              })}
              name="releaseDateEnd"
              initialValue={
                queryParams.releaseDateEnd
                  ? moment(queryParams.releaseDateEnd)
                  : undefined
              }
              rules={[
                (e) => {
                  const startDate = e.getFieldValue('releaseDateStart');
                  const endDate = e.getFieldValue('releaseDateEnd');

                  if (startDate && !endDate) {
                    return { required: true, message: 'Informe a data' };
                  }
                  return { required: false };
                },
              ]}
            >
              <InputDate disabledDate={(date) => date < startDate} />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={12} lg={4} xxl={4}>
            <Button
              type="primary"
              onClick={() => {
                resetDateError();
                remove();
                onCallback();
              }}
              size="large"
              block
              loading={isFetching}
            >
              {intl.formatMessage({
                id: 'purchaseReturn.form.button.updateData',
              })}
            </Button>
          </Col>

          {!!generateInvoiceId && (
            <>
              <Divider />

              <Col
                span={24}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <GenerateInvoiceButtons
                  generateInvoiceId={generateInvoiceId}
                  setGenerateInvoiceId={setGenerateInvoiceId}
                />
              </Col>
            </>
          )}
        </Row>

        {!generateInvoiceId && (
          <Form.List name="items" initialValue={[]}>
            {() => {
              if (items?.length) {
                return (
                  <>
                    <Divider />

                    <InfiniteTable<PurchaseItem>
                      rowSelection={{
                        onChange: (rowKeys, selecteds) => {
                          setRowKeys(rowKeys as number[]);
                          setHasKey(selecteds.some(({ hasKey }) => hasKey));
                          form.setFieldValue(
                            'items',
                            form
                              .getFieldValue('items')
                              .map((item: PurchaseItem) =>
                                !rowKeys.includes(item.id)
                                  ? {
                                      ...item,
                                      quantityToReturn: undefined,
                                    }
                                  : {
                                      ...item,
                                      quantityToReturn:
                                        (item.quantity ?? 0) -
                                        (item.amountReturned ?? 0),
                                    },
                              ),
                          );
                        },
                        selectedRowKeys: rowKeys,
                      }}
                      dataSource={form.getFieldValue('items')}
                      loading={isFetching}
                      columns={columns}
                      rowKey={(row) => row.id}
                      hasNextPage={hasNextPage}
                      fetchNextPage={fetchNextPage}
                      scroll={{ x: getTableScrollX(columns) }}
                    />

                    <Divider />
                  </>
                );
              } else {
                return (
                  <>
                    <Divider />

                    <Empty description="Não foi encontrado itens para realizar a devolução" />
                  </>
                );
              }
            }}
          </Form.List>
        )}
      </Form>

      <SuppliersSelectedModal
        visible={modalSupplierVisible}
        handleVisible={modalSupplierAction.toggle}
        noSelection
        onSupplierSelect={handleSupplierSelect}
      />
    </>
  );
}

export default PurchaseReturnForm;
